<template>
  <nav class="navbar" :class="{ scrolled: isScrolled }">
    <div class="nav-inner">
      <!-- Logo -->
      <a class="nav-logo" href="#">
        <span class="logo-m">M</span><span class="logo-rest">ARVEL</span>
        <span class="logo-tag">40M+</span>
      </a>

      <!-- Desktop Links -->
      <ul class="nav-links" :class="{ open: menuOpen }">
        <li><a href="#explorer" @click="menuOpen=false">Explorer</a></li>
        <li><a href="#citation" @click="menuOpen=false">Citation</a></li>
        <li class="nav-divider"></li>
        <li>
          <a href="https://arxiv.org/abs/2411.17945" target="_blank" class="nav-btn">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/></svg>
            Paper
          </a>
        </li>
        <li>
          <a href="https://github.com/SadilKhan/MARVEL-FX3D" target="_blank" class="nav-btn">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
            GitHub
          </a>
        </li>
        <li>
          <a href="https://huggingface.co/datasets/sankalpsinha77/MARVEL-40M" target="_blank" class="nav-btn nav-btn-hug">🤗 Dataset</a>
        </li>
        <li class="nav-divider"></li>
        <!-- Theme toggle -->
        <li>
          <button class="theme-toggle" @click="$emit('toggle-theme')" :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'">
            <span class="toggle-track">
              <span class="toggle-thumb"></span>
              <span class="toggle-icon-dark">🌙</span>
              <span class="toggle-icon-light">☀️</span>
            </span>
          </button>
        </li>
      </ul>

      <!-- Hamburger -->
      <div class="nav-right-mobile">
        <!-- Mobile theme toggle -->
        <button class="theme-toggle" @click="$emit('toggle-theme')">
          <span class="toggle-track">
            <span class="toggle-thumb"></span>
            <span class="toggle-icon-dark">🌙</span>
            <span class="toggle-icon-light">☀️</span>
          </span>
        </button>
        <button class="hamburger" @click="menuOpen = !menuOpen" :class="{ active: menuOpen }" aria-label="Menu">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'AppBar',
  props: {
    theme: { type: String, default: 'dark' },
  },
  data() {
    return { isScrolled: false, menuOpen: false };
  },
  computed: {
    isDark() { return this.theme === 'dark'; },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() { this.isScrolled = window.scrollY > 30; }
  }
};
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  padding: 0 2rem;
  transition: background 0.4s ease, box-shadow 0.4s ease;
}
.navbar.scrolled {
  background: var(--nav-bg);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 8px 32px var(--shadow);
}

.nav-inner {
  max-width: 1280px;
  margin: 0 auto;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

/* Logo */
.nav-logo {
  display: flex; align-items: baseline; gap: 0;
  text-decoration: none;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.6rem; letter-spacing: 0.05em;
  color: var(--text); flex-shrink: 0;
}
.logo-m {
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  font-size: 2rem;
}
.logo-rest { color: var(--text); }
.logo-tag {
  font-family: 'JetBrains Mono', monospace; font-size: 0.6rem;
  letter-spacing: 0.12em; color: var(--accent3); margin-left: 6px;
  padding: 2px 6px; border: 1px solid var(--accent3); border-radius: 4px;
  align-self: center;
}

/* Links */
.nav-links {
  display: flex; align-items: center; gap: 0.25rem; list-style: none;
}
.nav-links a {
  color: var(--text2); text-decoration: none;
  font-size: 0.875rem; font-weight: 500; letter-spacing: 0.04em;
  padding: 6px 14px; border-radius: 8px;
  transition: color var(--transition), background var(--transition);
}
.nav-links a:hover { color: var(--text); background: var(--surface2); }

.nav-divider {
  width: 1px; height: 20px;
  background: var(--border2); margin: 0 6px;
}

/* Nav buttons */
.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border2) !important;
  background: rgba(255,255,255,0.04) !important;
  color: var(--text2) !important;
  padding: 6px 13px !important;
  border-radius: 8px !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  transition: all var(--transition) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
}
.nav-btn:hover {
  background: rgba(255,255,255,0.09) !important;
  border-color: rgba(255,255,255,0.22) !important;
  color: var(--text) !important;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1) !important;
  transform: translateY(-1px);
}
.nav-btn-hug:hover { border-color: #ffa800 !important; box-shadow: 0 0 14px rgba(255,168,0,0.25) !important; }

/* ── Theme toggle ── */
.theme-toggle {
  background: none; border: none; cursor: pointer;
  padding: 4px; line-height: 0; display: flex; align-items: center;
}
.toggle-track {
  position: relative;
  width: 52px; height: 28px;
  background: var(--surface2);
  border: 1px solid var(--border2);
  border-radius: 100px;
  display: flex; align-items: center;
  padding: 0 5px;
  transition: border-color var(--transition), box-shadow var(--transition);
}
.theme-toggle:hover .toggle-track {
  border-color: var(--accent);
  box-shadow: 0 0 12px var(--glow);
}
.toggle-thumb {
  position: absolute;
  left: 4px;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  box-shadow: 0 2px 8px var(--glow);
  transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
}
/* Move thumb right in light mode */
[data-theme="light"] .toggle-thumb { transform: translateX(24px); }

.toggle-icon-dark,
.toggle-icon-light {
  font-size: 0.75rem;
  position: absolute;
  transition: opacity 0.25s ease;
  line-height: 1;
}
.toggle-icon-dark  { right: 6px;  opacity: 1; }
.toggle-icon-light { left: 6px;   opacity: 0; }
[data-theme="light"] .toggle-icon-dark  { opacity: 0; }
[data-theme="light"] .toggle-icon-light { opacity: 1; }

/* Hamburger */
.nav-right-mobile {
  display: none;
  align-items: center;
  gap: 10px;
}
.hamburger {
  display: flex; flex-direction: column; gap: 5px;
  background: none; border: none; cursor: pointer; padding: 8px;
}
.hamburger span {
  display: block; width: 22px; height: 2px;
  background: var(--text); border-radius: 2px; transition: all 0.3s;
}
.hamburger.active span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.hamburger.active span:nth-child(2) { opacity: 0; }
.hamburger.active span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

@media (max-width: 768px) {
  .nav-right-mobile { display: flex; }
  /* hide desktop toggle inside the dropdown */
  .nav-links li:last-child { display: none; }
  .nav-links {
    display: none;
    position: fixed;
    top: 68px; left: 0; right: 0;
    flex-direction: column; align-items: stretch;
    background: var(--nav-bg);
    backdrop-filter: blur(24px);
    border-bottom: 1px solid var(--border);
    padding: 1rem; gap: 0.25rem;
  }
  .nav-links.open { display: flex; }
  .nav-links a { padding: 12px 16px; font-size: 1rem; }
  .nav-divider { width: 100%; height: 1px; margin: 4px 0; }
}
</style>